import SBDropdown from '@Components/Dropdown';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import SbTextField from '@Components/Textfield';
import { shipmentAmtRefund } from '@Reducers/Admin';
import { TransactionsIdType } from '@Reducers/AdminShipment/Type';
import { getShipmentPaymentBySN } from '@Reducers/Shipment/actions';
import { AppDispatch, RootStateType } from '@Store';
import { Grid, Typography, alpha } from '@mui/material';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import Actions from '../Actions';

// const PAYMENT_OPTIONS: DropdownOptionType[] = [{ display: 'Wallet', value: 'wallet' }];
const modeOptionsMap: { [key: string]: DropdownOptionType } = {
  wallet: { display: 'Wallet', value: 'wallet' },
  gateway: { display: 'Gateway', value: 'gateway' },
  cash: { display: 'Cash', value: 'cash' },
};

interface Props {
  onCancel: () => void;
}

function Refund(props: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const shipmentPayment = useSelector((state: RootStateType) => state.adminShipment.shipmentpayment);
  // const { shipmentNumber, payableAmount } = shipmentPayment || {};
  const { shipmentNumber, payableAmount = 0, amount = 0, tdsAmount = 0, _transactionsId } = shipmentPayment || {};
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const { organizationData } = useSelector((state: RootStateType) => state?.orgSetup);
  const successfulTransactions = _transactionsId?.filter(payment => payment.status === 'successful') || [];
  const totalTdsAmount = successfulTransactions.reduce((sum, payment) => sum + payment.tdsAmount, 0);

  // const PAYMENT_OPTIONS: DropdownOptionType[] = useMemo(() => {
  //   if (!shipmentPayment?._transactionsId?.length) return [];
  //   return Object.values(
  //     shipmentPayment?._transactionsId.reduce((op, t) => {
  //       if (!op[t.mode] && t.paymentTransactionId && t.type !== 'refund')
  //         op[t.mode] = { ...modeOptionsMap[t.mode], metadata: t };
  //       return op;
  //     }, {} as { [key: string]: DropdownOptionType<TransactionsIdType> }),
  //   );
  // }, [shipmentPayment?._transactionsId]);
  const getLastSuccessfulTransaction = (transactions: TransactionsIdType[] = []): TransactionsIdType | undefined => {
    const successfulTransactions = transactions.filter(transaction => transaction.status === 'successful');
    return successfulTransactions[successfulTransactions.length - 1];
  };
  const PAYMENT_OPTIONS: DropdownOptionType[] = useMemo(() => {
    if (!shipmentPayment?._transactionsId?.length) return [];

    const optionsMap = shipmentPayment?._transactionsId.reduce((op, t) => {
      if (!op[t.mode] && t.paymentTransactionId && t.type !== 'refund') {
        op[t.mode] = { ...modeOptionsMap[t.mode], metadata: t };
      }
      return op;
    }, {} as { [key: string]: DropdownOptionType<TransactionsIdType> });

    // Add 'Cash' option if it doesn't exist
    if (!optionsMap['cash']) {
      const lastSuccessfulTransaction = getLastSuccessfulTransaction(shipmentPayment?._transactionsId);
      optionsMap['cash'] = {
        display: 'Cash',
        value: 'cash',
        metadata: lastSuccessfulTransaction,
      };
    }

    // Add 'Wallet' option if it doesn't exist
    if (!optionsMap['wallet']) {
      const lastSuccessfulTransaction = getLastSuccessfulTransaction(shipmentPayment?._transactionsId);
      optionsMap['wallet'] = {
        display: 'Wallet',
        value: 'wallet',
        metadata: lastSuccessfulTransaction,
      };
    }

    return Object.values(optionsMap);
  }, [shipmentPayment?._transactionsId, tdsAmount, totalTdsAmount]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      mode: '',
      amount: amount - (totalTdsAmount + tdsAmount) - (payableAmount - tdsAmount),
      comment: '',
    },
    validationSchema: Yup.object().shape({
      mode: Yup.string().required(),
      amount: Yup.number()
        .min(0)
        // .max(Math.abs(payableAmount || 0))
        .max(
          Math.abs(
            shipmentById && (shipmentById?.status === 'cancelled' || shipmentById?.status === 'void')
              ? amount || 0
              : payableAmount || 0,
          ),
        )
        .required('Amount is required.'),
      comment: Yup.string().trim().min(5).required('Comment is required.'),
    }),
    onSubmit(values) {
      // handleOnSubmit(values);

      if (shipmentNumber) {
        const selectedDropDownOption: DropdownOptionType | undefined = PAYMENT_OPTIONS.find(
          x => x.value === values.mode,
        );
        const metadata = selectedDropDownOption?.metadata as TransactionsIdType;

        if (!metadata?.paymentTransactionId) return;
        let idToUse;
        let isAccountHolder;
        if (shipmentById) {
          idToUse = shipmentById._accountId?.isAccountHolder
            ? shipmentById._accountId?._id
            : shipmentById?._userId?._id;
          isAccountHolder = shipmentById._accountId?.isAccountHolder;
        }

        dispatch(
          shipmentAmtRefund({
            amount: values.amount?.toString() || '',
            comment: values.comment,
            shipmentNumber: shipmentNumber,
            transactionId: metadata.paymentTransactionId,
            currency: organizationData?.currency?.name || 'INR',
            mode: values.mode || '',
            accountId: idToUse,
            isAccountHolder: isAccountHolder,
          }),
        ).then(res => {
          if (res.meta.requestStatus === 'fulfilled') {
            setTimeout(() => {
              dispatch(getShipmentPaymentBySN(shipmentNumber));
            }, 2000);
            props.onCancel();
          }
        });
      }
    },
  });

  return (
    <Grid
      container
      p={3}
      sx={{
        borderRadius: '12px',
        border: theme => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
      }}
    >
      <Grid container item mb={2}>
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>Refund</Typography>
      </Grid>
      <Grid container item spacing={2} mb={2}>
        <Grid item xs={12}>
          <SBDropdown
            id="paymentMode"
            options={PAYMENT_OPTIONS}
            label="Mode"
            name="mode"
            required
            value={formik.values.mode ? [formik.values.mode] : []}
            onChange={([value]) => {
              formik.setFieldValue('mode', value);
            }}
            disabled={!PAYMENT_OPTIONS.length}
            error={formik?.errors?.mode || ''}
          />
        </Grid>
        <Grid item xs={12}>
          <SbTextField
            label="Amount"
            required
            name="amount"
            value={formik.values.amount}
            type="number"
            number
            // disabled={!!formik.values.amount}
            error={formik?.errors?.amount || ''}
            onChange={formik.handleChange}
            disabled={!PAYMENT_OPTIONS.length}
          />
        </Grid>
        <Grid item xs={12}>
          <SbTextField
            label="Comment"
            name="comment"
            value={formik.values.comment}
            error={formik?.errors?.comment || ''}
            onChange={formik.handleChange}
            disabled={!PAYMENT_OPTIONS.length}
          />
        </Grid>
      </Grid>
      {PAYMENT_OPTIONS.length ? <Actions onCancel={props.onCancel} onSave={formik.handleSubmit} /> : <></>}
    </Grid>
  );
}
export default Refund;
